
import 'reflect-metadata';
import Vue from 'vue';
import Component from 'vue-class-component';
import PcSideChatsBanner from '@/components/chat/PcSideChatsBanner.vue';
import {NotificationManager} from '@/managers/notification-manager';
import {Http} from "@/constants/http";
import {Settings} from "@/constants/settings";
import {IntegrationUtils} from "@/utils/integration-utils";
import {Version} from "@/constants/version";
import {GameState} from "@/constants/game-state";
import ListenerManager from '@/managers/listener-manager';
import SceneUtils from "@/utils/scene-utils";
import ConnectionStore from "@/store/connection";
import PlayerStore from "@/store/player";
import SecurityStore from "@/store/security";
import CU from '../utils/c-u';
import {InterfaceMode, Lang} from "@/store/player/types";
import {Urls} from "@/constants/urls";
import {Keys} from "@/constants/key";
import SoundUtils from "@/utils/sound-utils";
import {ClanShortInfo} from "@/store/clan/types";
import {DomainUtils} from "@/utils/domain-utils";
import ViewportReport from "@/utils/viewport-report";
import AdvOverlay from "@/utils/adv-overlay";
import http from "@/axios";
import PromoUtils from "@/utils/promo-utils";
import {YaStorage} from "@/utils/ya-storage";
import {Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import {SceneService} from "@/services/scene-service";
import {RoutingBootstrapService} from "@/services/routing-bootstrap-service";
import {PikabuGuestSession} from "@/utils/pikabu-guest-session";

const RegisterPage = () => ({
  component: import('@/pages/RegisterPage.vue'),
})

const RatingPage = () => ({
  component: import('@/pages/RatingPage.vue'),
})
const ClanRatingPage = () => ({
  component: import('@/pages/ClanRatingPage.vue'),
})
const LoginPage = () => ({
  component: import('@/pages/LoginPage.vue'),
})
const PasswordResetPage = () => ({
  component: import('@/pages/PasswordResetPage.vue'),
})
const ChatPage = () => ({
  component: import('@/pages/ChatPage.vue'),
})
const MailPage = () => ({
  component: import('@/pages/MailPage.vue'),
})
const ConnectionPage = () => ({
  component: import('@/pages/ConnectionPage.vue'),
})
const RenamePage = () => ({
  component: import('@/pages/RenamePage.vue'),
})
const MailOrPhonePage = () => ({
  component: import('@/pages/MailOrPhonePage.vue'),
})
const PasswordPage = () => ({
  component: import('@/pages/PasswordPage.vue'),
})
const ClanNamePage = () => ({
  component: import('@/pages/ClanNamePage.vue'),
})
const ClanAboutPage = () => ({
  component: import('@/pages/ClanAboutPage.vue'),
})
const EditPage = () => ({
  component: import('@/pages/EditPage.vue'),
})
const ReloadPage = () => ({
  component: import('@/pages/ReloadPage.vue'),
})
const UpdateNotificationPage = () => ({
  component: import('@/pages/UpdateNotification.vue'),
})


@Component({
  components: {
    ReloadPage,
    PasswordPage,
    ClanNamePage,
    RenamePage,
    MailOrPhonePage,
    ConnectionPage,
    RegisterPage,
    LoginPage,
    PasswordResetPage,
    RatingPage,
    ClanRatingPage,
    ChatPage,
    MailPage,
    ClanAboutPage,
    EditPage,
    UpdateNotificationPage,
    PcSideChatsBanner
  }
})
export default class MainView extends Vue {
  private readonly PC_SIDE_CHAT_MIN_WIDTH: number = 1100;
  private downloaded: boolean = false;
  private gameInstance: any = null;
  private containerId: string = 'game-container';
  private readonly preventDesktopContextMenu = (event: Event) => {
    if (CU.isDesktop()) {
      event.preventDefault();
    }
  };
  // Отложенное применение нового размера: события resize идут пачками (панель браузера,
  // клавиатура, разворот мини-аппа), перестраивать сцену на каждое из них нельзя.
  private resizeSettleTimer: number | undefined = undefined;
  private lastSeenCssSize: string = '';

  private readonly handleWindowResize = () => {
    // Флаг ставим ВСЕГДА. Раньше событие, пришедшее при заблокированном канвасе
    // (открыты чат, почта, форум, рейтинг - там же появляется клавиатура и уезжает
    // панель браузера), просто терялось, и канвас до конца сессии оставался в старом
    // размере: низ интерфейса с героями уходил за экран либо снизу оставалась чёрная полоса.
    CU.WINDOW_RESIZED = true;
    this.scheduleCanvasResize();
  };
  private readonly handleFullscreenChange = () => {
    this.handleWindowResize();
  };

  private footerId: string = 'footer-container';
  private headerId: string = 'header-container';

  private chatRoom: string = Settings.MAIN_CHAT_ROOM;
  private header: string | undefined = '';
  private footer: string | undefined = '';

  private registerPageVisible: boolean = false;
  private ratingPageVisible: boolean = false;
  private clanRatingPageVisible: boolean = false;
  private loginPageVisible: boolean = false;
  private passwordResetPageVisible: boolean = false;
  private passwordResetToken: string = '';
  private chatPageVisible: boolean = false;
  private pcSideChatsOpen: boolean = false;
  private mailPageVisible: boolean = false;
  private connectionPageVisible: boolean = false;
  private reloadPageVisible: boolean = false;
  private renamePageVisible: boolean = false;
  private mailOrPhonePageVisible: boolean = false;
  private passwordPageVisible: boolean = false;
  private clanNamePageVisible: boolean = false;
  private clanAboutPageVisible: boolean = false;
  private editPageVisible: boolean = false;
  private updateNotificationPageVisible: boolean = false;

  private static loggedErrors: number = 0;

  private logClError(txt: string) {
    if (MainView.loggedErrors < 3) {
      PlayerStore.logClientError(txt);
      ++MainView.loggedErrors;
    }
  }

  protected created() {
    Settings.setLangDefaults();
    window.addEventListener('beforeunload', this.leaving);
    const passwordResetToken = new URLSearchParams(window.location.search).get('passwordResetToken');
    if (passwordResetToken) {
      this.passwordResetToken = passwordResetToken;
      this.passwordResetPageVisible = true;
    }
  }

  protected async mounted() {
    let self = this;
    window.addEventListener('contextmenu', this.preventDesktopContextMenu);

    if (IntegrationUtils.isYandexUser()) {
      try {
        // @ts-ignore
        const ysdk: any = await YaGames.init({ signed: true });
        // @ts-ignore
        window.ysdk = ysdk;
        Settings.applyYandexSdkLang(ysdk?.environment?.i18n?.lang);
        // @ts-ignore
        const player: any = await window.ysdk.getPlayer();
        Http.YA_UNIQUE_ID = player.getUniqueID();
        YaStorage.saveUniqueId(Http.YA_UNIQUE_ID);
        await SecurityStore.authenticateYandex();
        // LoadingAPI.ready() вызывается в BootScene после полной загрузки (GRA загружается раньше, чем игра доступна)
        self.init();
      } catch (e) {
        console.log('Yandex init/auth error', e);
        this.reloadPageVisible = true;
      }
    } else if (IntegrationUtils.isTelegramUser()) {
      // @ts-ignore
      window.Telegram.WebApp.ready();
      // @ts-ignore
      Http.TELEGRAM_ID = window.Telegram.WebApp.initDataUnsafe.user!.id;

      try {
        await SecurityStore.authenticateTelegram();
        self.init();
      } catch (e) {
        console.log('Telegram auth error', e);
        this.reloadPageVisible = true;
      }
    } else if (IntegrationUtils.isCrazyUser()) {

      try {
        // @ts-ignore
        await window.CrazyGames.SDK.init();

        // @ts-ignore
        let user = await window.CrazyGames.SDK.user.getUser();
        console.log("Get user result", user);

        if (!user) {
          GameState.REQUIRE_CRAZY_LOGIN = true;
          // @ts-ignore
          window.CrazyGames.SDK.game.loadingStart();
          await SecurityStore.logoutNoReload();
          self.init();
        } else {
          // @ts-ignore
          const token: any = await window.CrazyGames.SDK.user.getUserToken();
          console.log("Get token result", token);
          Http.CRAZY_ID = token;
          // @ts-ignore
          window.CrazyGames.SDK.game.loadingStart();
          await SecurityStore.authenticateCrazy();
          self.init();
        }
      } catch (e) {
        console.log("CrazyGames init/auth error:", e);
        this.reloadPageVisible = true;
      }

    } else if (IntegrationUtils.isKongUser()) {

      // @ts-ignore
      kongregateAPI.loadAPI(function(){
        // @ts-ignore
        window.kongregate = kongregateAPI.getAPI();

        // @ts-ignore
        let kongId = window.kongregate.services.getUserId();
        // @ts-ignore
        let userName = window.kongregate.services.getUsername();

        // @ts-ignore
        let token = window.kongregate.services.getGameAuthToken();

        Http.KONG_ID = kongId;

        // @ts-ignore
        if (window.kongregate.services.isGuest()) {
          GameState.REQUIRE_KONG_LOGIN = true;
          Http.KONG_ID = null;

          SecurityStore.authenticateKong(kongId, userName, null).then(() => {
            self.init();
          }).catch((e) => {
            console.log('Kong auth error', e);
            self.reloadPageVisible = true;
          });
        } else {
          SecurityStore.authenticateKong(kongId, userName, token).then(() => {
            self.init();
          }).catch((e) => {
            console.log('Kong auth error', e);
            self.reloadPageVisible = true;
          });
        }
      });

    } else if (IntegrationUtils.isMystoreUser()) {
      IntegrationUtils.initMystore();
      this.init();
    } else if (IntegrationUtils.isExeUser()) {
      IntegrationUtils.initExeAPI();
      this.init();
    } else if (IntegrationUtils.isGPUser()) {
      (window as any).onGPInit = async (gp: any) => {
        try {
          await Promise.race([
            gp.player.ready,
            new Promise((_, reject) => setTimeout(() => reject(new Error('GP player.ready timeout')), 5000))
          ]);
        } catch (e) {
          console.log('GP player.ready error', e);
          this.reloadPageVisible = true;
          return;
        }
        this.setupGPHeaders(gp);
        const isPikabu = this.isPikabuGP(gp);
        Http.PIKABU_GUEST = isPikabu && (!gp.player.isLoggedIn || !gp.player.hasAnyCredentials);
        if (isPikabu) {
          const guestAuthorization = PikabuGuestSession.getAuthorization();
          if (guestAuthorization && !Http.AUTHORIZATION) {
            Http.AUTHORIZATION = guestAuthorization;
          }
        }

        (window as any).gp = gp;
        gp.gameStart();
        let playerId = gp.player.id;
        await PlayerStore.fetchPlayerInfoCurrent(false);
        const resumedPikabuGuest = isPikabu && Http.PIKABU_GUEST && !!PlayerStore.getPlayer();

        if (isPikabu && gp.player.isLoggedIn && gp.player.hasAnyCredentials) {
          const migrationResult = await SecurityStore.migratePikabuGPGuest(playerId);
          if (migrationResult === "Player already exists") {
            await SecurityStore.logoutNoReload();
          }
        }

        try {
          if (!resumedPikabuGuest) {
            await SecurityStore.authenticateGP(playerId, gp.player.name);
          }
          await PlayerStore.fetchPlayerInfoCurrent(false);
          if (isPikabu && gp.player.isLoggedIn && gp.player.hasAnyCredentials) {
            PikabuGuestSession.clearAuthorization();
            Http.PIKABU_GUEST = false;
          }
          self.init();
        } catch (e) {
          console.log('GP auth error', e);
          this.reloadPageVisible = true;
          return;
        }
        gp.gameplayStart();

        gp.player.on("login", async (success: any) => {
          console.log("GP login");
          if (!success) {
            console.log("GP login failed");
            return;
          }

          try {
            const oldPlayerId = playerId;
            await Promise.race([
              gp.player.ready,
              new Promise((_, reject) => setTimeout(() => reject(new Error('GP player.ready timeout after login')), 5000))
            ]);
            this.setupGPHeaders(gp);
            playerId = gp.player.id;

            if (this.isPikabuGP(gp)) {
              const migrationResult = await SecurityStore.migratePikabuGPGuest(playerId);
              if (migrationResult === "Player already exists") {
                await SecurityStore.logoutNoReload();
              }
              await SecurityStore.authenticateGP(playerId, gp.player.name);
              PikabuGuestSession.clearAuthorization();
              Http.PIKABU_GUEST = false;
              await PlayerStore.fetchPlayerInfoCurrent(false);
              self.init();
              return;
            }

            if (String(oldPlayerId) === String(playerId) && gp.player.isLoggedIn) {
              await SecurityStore.logout();
              return;
            }

            const rs = await SecurityStore.loginGPUser(oldPlayerId, playerId);
            console.log("SecurityStore.loginGPUser: ", rs);
            if (rs === "Player already exists") {
              await SecurityStore.logout();
              return;
            }
          } catch (e) {
            console.log('GP login error', e);
            this.reloadPageVisible = true;
          }
        });

        gp.player.on("logout", (success: any) => {
          console.log("GP logout");
          if (success) {
            SecurityStore.logout().then( () => {
              console.log("SecurityStore.logout");
              self.init();
              window.location.reload();
            });
          } else {
            console.log("GP logout failed");
          }
        });

        // Поставили на паузу
        gp.on("pause", () => {
          SoundUtils.soundOff();
          (window as any).pauseAllAudio();
        });

        // Возобновили игру
        gp.on("resume", () => {
          SoundUtils.soundOn();
          (window as any).resumeAllAudio();
        });

        gp.fullscreen.on('open', () => this.resizeWindow());
        gp.fullscreen.on('close', () => this.resizeWindow());
        gp.fullscreen.on("change", () => this.resizeWindow());

      };
    } else if (IntegrationUtils.isPGUser()) {
      try {
        // @ts-ignore
        if (window.bridge && typeof window.bridge.initialize === 'function') {
          // @ts-ignore
          await window.bridge.initialize();
        }
        // @ts-ignore
        const b = window.bridge;
        Http.PG_PLATFORM = b.platform.id;

        // Ensure authorization if supported
        const supportsAuth = !!(b?.player?.isAuthorizationSupported);
        let isAuthorized = !!(b?.player?.isAuthorized);
        if (supportsAuth && !isAuthorized && typeof b?.player?.authorize === 'function') {
          try {
            await b.player.authorize({});
            isAuthorized = !!(b?.player?.isAuthorized);
          } catch (authErr) {
            console.log('PG authorize error', authErr);
            this.init();
            return;
          }
        }

        if (supportsAuth && !isAuthorized) {
          this.init();
          return;
        }

        const playerId = b?.player?.id || null;
        const playerName = b?.player?.name || '';
        const jwt = b?.player?.extra?.jwt || null;
        if (playerId) {
          Http.PG_PLAYER_ID = playerId;
          SecurityStore.authenticatePG(playerId, playerName, jwt).then(async () => {
            try {
              if (b?.payments?.isSupported && typeof b.payments.getPurchases === 'function') {
                const purchases = await b.payments.getPurchases();
                if (Array.isArray(purchases)) {
                  for (const purchase of purchases) {
                    if (purchase?.id) {
                      try {
                        const orderId = purchase?.orderId || purchase?.jwt?.orderId || undefined;
                        const externalId = purchase?.externalId || purchase?.jwt?.externalId || undefined;
                        await SecurityStore.buyPlaygama(purchase.id, orderId, externalId);
                        if (typeof b.payments.consumePurchase === 'function') {
                          await b.payments.consumePurchase(purchase.id);
                        }
                      } catch (ignore) {}
                    }
                  }
                }
              }
            } catch (ignore) {}
            b.platform.sendMessage("game_ready");
            self.init();
          })
        }
      } catch (e) {
        console.log('PG init/auth error', e);
      }
    } else if (IntegrationUtils.isPlayMarketUser()) {
      Http.PLAYMARKET = 'playmarket_unique_id';
      self.init();
    } else {
      this.init();
    }
  }

  private resizeWindow() {
    CU.WINDOW_RESIZED = true;
    this.scheduleCanvasResize();
  }

  /** Ждём, пока размер устоится, и только потом трогаем канвас. */
  private scheduleCanvasResize(delay: number = 400) {
    window.clearTimeout(this.resizeSettleTimer);
    this.resizeSettleTimer = window.setTimeout(() => this.applyCanvasResize(), delay);
  }

  private applyCanvasResize() {
    const size = CU.cssWidth() + 'x' + CU.cssHeight();
    if (size !== this.lastSeenCssSize) {
      // Размер ещё меняется: панель браузера уезжает, мини-апп разворачивается,
      // идёт поворот экрана. Перестраиваться посреди анимации бессмысленно.
      this.lastSeenCssSize = size;
      this.scheduleCanvasResize();
      return;
    }

    if (!CU.canvasSizeOutdated()) {
      CU.WINDOW_RESIZED = false;
      return;
    }

    // Экранная клавиатура сжимает вьюпорт на время ввода - это не новый размер окна.
    // Поверх канваса открыта HTML-страница - перестроим сцену, когда её закроют.
    // Идёт реклама: на мобиле она сама двигает вьюпорт и после закрытия обычно возвращает
    // его, а перестроение посреди показа сносит открытые панели.
    if (MainView.isTextInputFocused() || CU.isCanvasBlocked() || AdvOverlay.isActive()) {
      this.scheduleCanvasResize(1000);
      return;
    }

    let scene = SceneUtils.getCurrentScene();
    //@ts-ignore
    if (!scene || !scene.scene.isActive() || typeof scene.reload !== 'function') {
      // Сцена ещё грузится: размер применится в её preload через checkCanvasSize.
      this.scheduleCanvasResize(1000);
      return;
    }

    CU.checkCanvasSize(scene, () => {
      CU.WINDOW_RESIZED = false;
      ViewportReport.log('resized');
      //@ts-ignore
      scene.reload();
    });
  }

  private static isTextInputFocused(): boolean {
    const el = document.activeElement as HTMLElement | null;
    return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
  }

  private setupGPHeaders(gp: any) {
    Http.GP_PLAYER_ID = gp.player.id;
    console.log('GP player ID: ' + Http.GP_PLAYER_ID);
    Http.GP_PLATFORM = gp.platform?.adapter?.type || "gp";
    console.log('GP platform: ' + Http.GP_PLATFORM);
    console.log('GP name: ' + gp.player.name);
  }

  private isPikabuGP(gp: any): boolean {
    if (gp.platform?.adapter?.type !== "PARTNER" || gp.platform?.tag !== "gp-1") {
      return false;
    }

    const ancestorOrigins = (window.location as any).ancestorOrigins;
    if (!ancestorOrigins) {
      return false;
    }

    return (Array.from(ancestorOrigins as any) as string[])
        .some(origin => origin === "https://games.pikabu.ru");
  }

  protected leaving() {
    // PlayerStore.logUnload(GameState.LOADED_PCT);
  }

  private async init() {
    let self = this;

    (function(){

      var originallog = console.error;

      console.error = function(txt) {
        self.logClError(txt);
        originallog.apply(console, [txt])
      }

    })();

    window.addEventListener("error", function (e) {
      // Some third-party/injected scripts may throw when their SDK isn't available (e.g. undefined.fireEvent).
      // We don't want to spam client error logs with these non-actionable errors.
      const rawMsg = `${(e as any)?.message || ''} ${(e as any)?.error?.message || ''}`;
      const isFireEventUndefined =
        rawMsg.includes("reading 'fireEvent'") ||
        rawMsg.includes('reading "fireEvent"') ||
        rawMsg.includes("Cannot read property 'fireEvent'") ||
        rawMsg.includes('Cannot read property "fireEvent"');
      if (isFireEventUndefined) {
        return false;
      }
      let message = (String(Version.VERSION_NUM) + ' --- ');
      let scene: Scene | undefined = SceneUtils.getCurrentScene();
      if (scene && scene.scene && scene.scene.key) {
        message += (scene.scene.key + ' : ');
      }
      if (e) {
        if (e.message) {
          message += (e.message + '  ====  ');
        }
        if (e.error && e.error.message) {
          message += (e.error.message);
        }
        if (e.filename) {
          message += ':::';
          message += String(e.filename);
        }
        if (e.lineno) {
          message += ':::';
          message += String(e.lineno);
        }
        if (e.colno) {
          message += ':';
          message += String(e.colno);
        }
      }
      self.logClError(message);
      return false;
    });

    let timerId = setInterval(() => {
      if (Settings.LOGGED_GAME_STARTED) {
        clearInterval(timerId);
      } else {
        if (document && document.hidden) {
          // PlayerStore.logHidden();
        } else {
          // PlayerStore.logActive();
        }
      }
    }, 5000);

    // iOS/WebKit leaves the Web Audio context a silent "zombie" after backgrounding; the
    // only reliable recovery is recreating it from a user gesture. Register those listeners.
    SoundUtils.installResumeGuards();

    document.addEventListener("visibilitychange", function(){
      if (document.hidden) {
        SoundUtils.soundOff();
      } else {
        SoundUtils.soundOn();
      }
    });

    document.addEventListener("onPause", () => {
      SoundUtils.soundOff();
    }, false);

    document.addEventListener("onResume", () => {
      SoundUtils.soundOn();
    }, false);

    const load = async () => {
      while (true) {
        try {
          return await import(/* webpackChunkName: "game" */ '@/game/game')
        } catch (e) {
          console.log('Import error. Retrying');
          console.log(e);
        }
      }
    };

    if (RoutingBootstrapService.isMobileApp()) {
      try {
        await RoutingBootstrapService.bootstrap();
      } catch (e) {
        console.log('Routing bootstrap error, using build-time API host', e);
      }
    }

    const fetchPlayer = async () => {
      let attempts = 0;
      while (true) {
        try {
          await PlayerStore.fetchPlayerInfoCurrent(true);
          let player = PlayerStore.getPlayer();
          if (player && RoutingBootstrapService.isMobileApp()) {
            // Existing account → server is already fixed.
            RoutingBootstrapService.lockServer();
          }
          if (player && player.interfaceMode === InterfaceMode.ORIGINAL) {
            for (let road of Keys.ROADS_UI) {
              Urls.IMAGES.set(road, `assets/bg/ui1/${road}.jpg`);
            }
            Settings.KNIGHT_OFFSET_X = 0;
          }
          if (IntegrationUtils.isYandexUser() || IntegrationUtils.isMystoreUser()) {
            let mLevel = PlayerStore.getMaxLevelAmongAccs()
            if (!!mLevel) {
              YaStorage.updateMaxLevel(mLevel);
            }

            let idToStore = PlayerStore.getIdToStore();
            if (!!idToStore) {
              YaStorage.saveUniqueId(idToStore);
            }
          }
          if (player && IntegrationUtils.isRustoreUser()) {
            IntegrationUtils.initRustorePaymentApi();
          }
          if (player && IntegrationUtils.isPlayMarketUser()) {
            IntegrationUtils.initPlaymarketPaymentApi();
          }
          return;
        } catch (e) {
          ++attempts;
          if (attempts > 3) {
            this.connectionPageVisible = true;
            break;
          } else {
            console.log('Player fetch error. Retrying');
            console.log(e);
          }
        }
      }
    };

    ListenerManager.setSessionCreateCallback(() => {
      NotificationManager.initNotifications();
    });

    ListenerManager.setConnectionLostCallback(() => {
      console.log('Lost connection');
      ConnectionStore.checkServerConnection().catch(() => {
        this.connectionPageVisible = true;
        self.logClError('Server connection error');
      });
    });

    let [game, pl] = await Promise.all([load(), fetchPlayer()]);

    this.downloaded = true;

    if (IntegrationUtils.isRustoreUser() && IntegrationUtils.getRustoreAppVersion() < 34){
      this.updateNotificationPageVisible = true;
    }

    this.header = PlayerStore.getHeader();
    this.footer = PlayerStore.getFooter();

    this.$nextTick(() => {

      this.gameInstance = game.launch(this.containerId, this.headerId, this.footerId);

      const canvas = <HTMLElement> document.getElementsByTagName('canvas').item(0);
      canvas.style.width = String(CU.CSS_WIDTH) + 'px';
      canvas.style.height = String(CU.CSS_HEIGHT) + 'px';

      // Размер меряется здесь один раз, а видимая область к этому моменту ещё не устоялась:
      // в оболочке портала (Яндекс.Игры, VK) доезжает панель и меняется размер айфрейма,
      // а в приложении Capacitor уходит в полный экран уже из PreloadScene. Событие resize
      // при этом может прийти до того, как слушатели ниже подпишутся, поэтому перепроверяем сами.
      this.scheduleCanvasResize(1500);

      // Диагностика размеров у VK-игроков: сразу после старта и когда всё точно устоялось.
      window.setTimeout(() => ViewportReport.log('start'), 3000);
      window.setTimeout(() => ViewportReport.log('settled'), 15000);

      ListenerManager.setRegisterCallback(() => {
        this.registerPageVisible = true;
      });

      ListenerManager.setLoginCallback(() => {
        this.loginPageVisible = true;
      });

      ListenerManager.setRatingCallback(() => {
        this.ratingPageVisible = true;
      });

      ListenerManager.setClanRatingCallback(() => {
        this.clanRatingPageVisible = true;
      });

      ListenerManager.setChatOpenCallback((chatRoom: string) => {
        // На широком экране (PC) кнопка "чат" должна открывать/закрывать боковые чаты
        // и НЕ открывать обычный чат.
        if (window.innerWidth >= this.PC_SIDE_CHAT_MIN_WIDTH) {
          this.pcSideChatsOpen = !this.pcSideChatsOpen;
          // `MenuService.openChatPage()` блокирует canvas перед коллбеком.
          // Для боковых чатов блокировка не нужна.
          CU.unblockCanvas();
          this.chatPageVisible = false;
          return;
        }

        this.chatRoom = chatRoom;
        this.chatPageVisible = true;
      });

      ListenerManager.setMailCallback(() => {
        this.mailPageVisible = true;
      });

      ListenerManager.setRenamePageOpenCallback(() => {
        this.renamePageVisible = true;
      });

      ListenerManager.setChangeEmailOrPhonePageOpenCallback(() => {
        this.mailOrPhonePageVisible = true;
      });

      ListenerManager.setPasswordPageOpenCallback(() => {
        this.passwordPageVisible = true;
      });

      ListenerManager.setClanNamePageOpenCallback(() => {
        this.clanNamePageVisible = true;
      });

      ListenerManager.setClanAboutPageOpenCallback(() => {
        this.clanAboutPageVisible = true;
      });

      ListenerManager.setEditPageOpenCallback(() => {
        this.editPageVisible = true;
      });

      ListenerManager.setEditPageCloseCallback(() => {
        this.editPageVisible = false;
        CU.unblockCanvas();
      });

      (window as any).onPlaymarketAuthSuccess = async () => {
        try {
          await PlayerStore.fetchPlayerInfoCurrent(true);
          if (this.loginPageVisible) {
            this.closeLoginPage();
          }
          if (this.registerPageVisible) {
            this.closeRegisterPage();
          }
          ListenerManager.callLoginSuccessfulCallback();
        } catch (e) {
          console.error('onPlaymarketAuthSuccess error:', e);
          if (this.loginPageVisible) {
            this.closeLoginPage();
          }
          if (this.registerPageVisible) {
            this.closeRegisterPage();
          }
        }
      };

      (window as any).onPlaymarketAuthError = (message: string) => {
        const text = message || 'Ошибка авторизации';

        if (this.loginPageVisible) {
          const page = this.$refs.loginPage as any;
          if (page && typeof page.setAuthError === 'function') {
            page.setAuthError(text);
            CU.unblockCanvas();
            return;
          }
        }

        if (this.registerPageVisible) {
          const page = this.$refs.registerPage as any;
          if (page && typeof page.setAuthError === 'function') {
            page.setAuthError(text);
            CU.unblockCanvas();
            return;
          }
        }

        console.warn('[Playmarket] auth error received, but no auth page is open:', text);
        this.closeLoginPage();
      };

      this.$root.$on("openProfileCallback", (playerId: number) => {
        ListenerManager.callProfileCallback(playerId);
        this.closeRatingPage();
        this.closeClanRatingPage();
        this.closeChatPage();
        this.closeMailPage();
      })

      this.$root.$on("showClanDetailsPanel", (ci: ClanShortInfo) => {
        ListenerManager.callClanDetailsCallback(ci);
        this.closeRatingPage();
        this.closeClanRatingPage();  //TODO надо не закрывать панель, а отображать поверх (см. openClanDetailsPanel)
        this.closeChatPage();
        this.closeMailPage();
      })

      window.addEventListener("resize", this.handleWindowResize, false);
      window.visualViewport?.addEventListener("resize", this.handleWindowResize);
      document.addEventListener("fullscreenchange", this.handleFullscreenChange);
      document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange);

      if (IntegrationUtils.isYandexUser()) {
        try {
          // @ts-ignore
          window.ysdk.on('BONUS_AWARDED', (obj: any) => {
            try {
              if (!!obj && obj.hasOwnProperty('signature')) {
                console.log('obj.signature =' + obj.signature);
                PromoUtils.handleYaPromocode(obj.signature);
              } else {

                // @ts-ignore
                window.ysdk.bonuses.getPlayerBonuses().then((obj: any) => {
                  try {
                    if (!!obj && obj.hasOwnProperty('signature')) {
                      console.log('obj.signature =' + obj.signature);
                      PromoUtils.handleYaPromocode(obj.signature);
                    } else {
                      console.log('No obj signature recieved');
                      console.log(obj);
                      console.log('==========');
                    }
                  } catch (ignore) {}
                });

              }
            } catch (ignore) {}
          });
        } catch (ignore) {}
      }
    });

    IntegrationUtils.importPaymentSDK(PlayerStore.getPlayer());

    ListenerManager.setCloseChatCallback(() => {
      this.closeChatPage();
      this.closeMailPage();
    });

    let player = PlayerStore.getPlayer();
    if (!!player && DomainUtils.canChangeDomain(player)) {
      PlayerStore.fetchPlayerId(DomainUtils.getDirectDomain()).then(id => {
        if (player.id === id) {
          console.log('IDSs are the same. Switching to direct domain');
          http.defaults.baseURL = DomainUtils.getDirectDomain();
          console.log('set axios.defaults.baseURL to ' + http.defaults.baseURL);
        } else {
          console.log('Not getting same player ID from direct domain. Not Switching');
        }
      }).catch(function (error) {
        console.log('Cannot get player ID from direct domain. Not Switching');
        return 0;
      });
    }
  }

  protected destroyed() {
    window.clearTimeout(this.resizeSettleTimer);
    window.removeEventListener('contextmenu', this.preventDesktopContextMenu);
    window.removeEventListener("resize", this.handleWindowResize, false);
    window.visualViewport?.removeEventListener("resize", this.handleWindowResize);
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange);
    this.gameInstance.destroy(false);
  }

  protected closeRegisterPage() {
    this.registerPageVisible = false;
    CU.unblockCanvas();
  }

  protected closeLoginPage() {
    this.loginPageVisible = false;
    CU.unblockCanvas();
  }

  protected openPasswordResetPage() {
    this.loginPageVisible = false;
    this.passwordResetToken = '';
    this.passwordResetPageVisible = true;
  }

  protected closePasswordResetPage() {
    this.passwordResetPageVisible = false;
    CU.unblockCanvas();
  }

  protected closeRatingPage() {
    this.ratingPageVisible = false;
    CU.unblockCanvas();
  }

  protected closeClanRatingPage() {
    this.clanRatingPageVisible = false;
    CU.unblockCanvas();
  }

  protected async closeChatPage() {
    this.chatPageVisible = false;
    await ListenerManager.callChatSeenCallback(false);
    CU.unblockCanvas();
  }

  protected async closeMailPage() {
    this.mailPageVisible = false;
    await PlayerStore.fetchPlayerInfoCurrent();
    CU.unblockCanvas();
  }

  protected closeRenamePage() {
    this.renamePageVisible = false;
    CU.unblockCanvas();
  }

  protected closeEmailOrPhonePage() {
    this.mailOrPhonePageVisible = false;
    CU.unblockCanvas();
  }

  protected closePasswordPage() {
    this.passwordPageVisible = false;
    CU.unblockCanvas();
  }

  protected closeClanNamePage() {
    this.clanNamePageVisible = false;
    CU.unblockCanvas();
  }

  protected closeClanAboutPage() {
    this.clanAboutPageVisible = false;
    ListenerManager.callClanAboutPageCloseCallback()
    CU.unblockCanvas();
  }

  protected closeEditPage() {
    this.editPageVisible = false;
    ListenerManager.callEditPageCloseCallback();
    CU.unblockCanvas();
  }

  protected closeUpdateNotificationPage() {
    this.updateNotificationPageVisible = false;
    CU.unblockCanvas();
  }
}
